'use client';

import { useCallback, useMemo, useState } from 'react';
import { ROOT_PAGE, type CommandDef, type MenuPage } from '../types';

/**
 * Page stack for the command menu. The root page is implicit — the stack
 * holds only pushed sub-pages, so `pop()` on an empty stack is a no-op and
 * `page` always resolves to a valid page.
 */
type MenuStackState = {
  page: MenuPage;
  stack: MenuPage[];
  push: (next: MenuPage) => void;
  pop: () => void;
  reset: (pages?: MenuPage[]) => void;
};

export function useMenuStack(): MenuStackState {
  const [stack, setStack] = useState<MenuPage[]>([]);

  const page = useMemo<MenuPage>(
    () => stack[stack.length - 1] ?? ROOT_PAGE,
    [stack]
  );

  const push = useCallback((next: MenuPage) => {
    setStack((current) => [...current, next]);
  }, []);

  const pop = useCallback(() => {
    setStack((current) => current.slice(0, -1));
  }, []);

  const reset = useCallback((pages: MenuPage[] = []) => {
    setStack(pages);
  }, []);

  return { page, stack, push, pop, reset };
}

/**
 * A picker page is stale once the command that opened it is gone, e.g. the
 * page that registered it unmounted, leaving its callbacks dangling. Quick
 * actions (Change language) never register with a page, so callers pass
 * them alongside the page-registered commands.
 */
export function isPickerPageStale(
  page: MenuPage,
  availableCommands: readonly CommandDef[]
): boolean {
  if (page.kind !== 'picker') return false;
  return !availableCommands.some((command) => command.id === page.commandId);
}
